/**
 * Advocate landing page (`/pre-advokatov` / `/for-lawyers`) — publication gates
 * (issue #7230, spec `2026-07-05-advocate-landing-page-design.md` L4; launch
 * checklist §3 tracked in #7951).
 *
 * The page carries a legal marketing claim (režim (c) / čl. 28 GDPR DPA) whose
 * final wording is still gated on @citbaj's sign-off, and whose public launch is
 * a scheduled GO/NO-GO decision (#7951, ~18–24 Aug 2026). Two SEPARATE gates,
 * because production has already taken the first decision and not the second:
 *
 *   1. `ADVOCATE_LANDING_ENABLED` — **reachability**. Whether the route renders
 *      at all, or `notFound()`s. Verified **already `true` in production**
 *      (2026-08-04): `/sk/pre-advokatov` serves the full page today. It is
 *      `noindex, nofollow` and absent from `sitemap.xml`, so nothing links or
 *      indexes it — a deliberate soft-live state.
 *   2. `ADVOCATE_LANDING_PUBLISHED` — **discoverability**. The §3 launch
 *      checklist: sitemap entry, indexable metadata, header/footer navigation
 *      links, and the `/pre-advokata` alias repoint. Default OFF. Flipping this
 *      is what actually announces the page to users and to Google.
 *
 * Keeping them separate is load-bearing. The spec assumed one flip would do
 * both, but production opened reachability on its own; deriving the checklist
 * from gate 1 would mean the next release silently submitted an unratified
 * marketing claim to the index. Gate 2 is the real launch switch, and it is the
 * one that belongs to the GO/NO-GO.
 *
 * `isAdvocateLandingPublished()` **implies** `isAdvocateLandingEnabled()` — it
 * can never announce a route that 404s. That invariant is why every checklist
 * surface reads the published gate and not the raw env.
 *
 * ## Launching (gate 2): set the flag in BOTH env sources
 *
 * One value, but two places read it, because the surfaces are not all baked at
 * the same time:
 *
 *   - **Build-time (image bake).** `/for-lawyers` is statically prerendered, so
 *     its metadata freezes whatever the *build* saw. Source: the `FRONTEND_ENV`
 *     GitHub secret, written to `frontend/.env` before `docker build`.
 *   - **Runtime (container process).** The standalone image does not carry
 *     `frontend/.env`; the container gets its env from the prod VPS
 *     `/opt/lexomat/repo/frontend/.env` (`env_file` in `docker-compose.yml`).
 *     `sitemap.xml` is ISR, so its entry is re-evaluated in the running
 *     container, and the `(lagacy-footer)` group has `force-dynamic` routes
 *     whose header/footer render per request.
 *
 * So: add `ADVOCATE_LANDING_PUBLISHED=true` to the `FRONTEND_ENV` GitHub secret
 * **and** to the prod VPS `/opt/lexomat/repo/frontend/.env`, then run a release
 * build. Setting only the secret makes the page indexable but leaves it out of
 * `sitemap.xml` after the first revalidation and hides the nav links on the
 * dynamic routes; setting only the prod VPS `.env` does the reverse.
 *
 * Read both values only through these helpers. Bracket access is required —
 * tsconfig's `noPropertyAccessFromIndexSignature` applies to `process.env`'s
 * index signature — and neither has a `NEXT_PUBLIC_` prefix, so client
 * components receive the resolved boolean as a prop from a server component
 * rather than reading `process.env` themselves.
 */
export function isAdvocateLandingEnabled(
  value: string | undefined = process.env['ADVOCATE_LANDING_ENABLED']
): boolean {
  return value === 'true';
}

/**
 * Whether the advocate landing page is publicly ANNOUNCED — sitemap entry,
 * indexable metadata, navigation links, `/pre-advokata` alias repoint.
 *
 * Implies {@link isAdvocateLandingEnabled}: a page that 404s must never be
 * indexed, linked or listed, so a `PUBLISHED=true` set against a closed
 * reachability gate resolves to `false` rather than advertising a dead URL.
 */
export function isAdvocateLandingPublished(
  value: string | undefined = process.env['ADVOCATE_LANDING_PUBLISHED'],
  enabled: boolean = isAdvocateLandingEnabled()
): boolean {
  return value === 'true' && enabled;
}

/**
 * Canonical (locale-agnostic) pathname for the advocate landing page, matching
 * the `next-intl` `routing.pathnames` key. Localized slugs:
 *   en → /for-lawyers
 *   sk → /pre-advokatov
 */
export const ADVOCATE_LANDING_ROUTE = '/for-lawyers' as const;
